import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/routes/shared/_api'
import { useToast } from '@/hooks/use-toast'
import { InvestmentDataTypes } from '../api/schema'

interface ListInvestmentMenuProps {
  url: string
  listName: string
  ioUuid: string
}

type GroupedInvestments = Record<string, InvestmentDataTypes[]>

export const ListInvestmentMenu = ({
  url,
  listName,
  ioUuid,
}: ListInvestmentMenuProps) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return (
    <Popover>
      <PopoverTrigger>
        <i className="px-4 md:px-6 md:py-4 fa-regular fa-ellipsis-vertical"></i>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={-55}
        avoidCollisions={false}
        className="w-[187px] text-base px-0 py-2 rounded flex flex-col"
      >
        <Dialog>
          <DialogTrigger asChild>
            <div className="w-full text-left px-4 py-3 hover:bg-bg-light cursor-pointer">
              Remove from List
            </div>
          </DialogTrigger>
          <DialogContent
            aria-describedby={undefined}
            className="text-content-black text-left"
          >
            <DialogTitle className="text-content-black font-medium text-[22px] mb-10">
              Remove this investment?
            </DialogTitle>
            <div className="text-16 mb-10">
              The investment will be removed from your {listName}.
            </div>
            <div className="flex gap-4">
              <DialogClose asChild>
                <Button
                  variant="RM"
                  size="RM"
                  onClick={() => {
                    // Snapshot all investment caches before mutating
                    const previousCaches =
                      queryClient.getQueriesData<GroupedInvestments>({
                        queryKey: ['investments'],
                      })

                    // Optimistically remove from cache immediately
                    queryClient.setQueriesData<GroupedInvestments>(
                      { queryKey: ['investments'] },
                      (old) => {
                        if (!old?.[listName]) return old
                        return {
                          ...old,
                          [listName]: old[listName].filter(
                            (inv) => inv.io_uuid !== ioUuid
                          ),
                        }
                      }
                    )

                    toast({
                      description: `Investment removed from ${listName}`,
                      variant: 'success',
                      position: 'top',
                    })

                    // Fire the request in the background
                    const request =
                      listName === 'watchlist' ? api.delete(url) : api.get(url)
                    request
                      .catch(() => {
                        // Revert on error — restore previous cache
                        previousCaches.forEach(([key, data]) => {
                          queryClient.setQueryData(key, data)
                        })
                        toast({
                          description: `Failed to remove investment from ${listName}`,
                          variant: 'error',
                          position: 'top',
                        })
                      })
                      .finally(() => {
                        queryClient.invalidateQueries(['investments'])
                      })
                  }}
                >
                  Yes - remove it
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button variant="RMSecondary" size="RMSecondary">
                  No - keep it
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </PopoverContent>
    </Popover>
  )
}
